import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Plus, Monitor,Edit2, GripVertical, LayoutGrid, Film, MoreVertical, Plug, Activity } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import devLog from '../utils/devLog';

function Screens() {
  const [screens, setScreens] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [newScreenName, setNewScreenName] = useState('');
  const [pairCode, setPairCode] = useState('');
  const [pairingError, setPairingError] = useState('');
  const [isPairing, setIsPairing] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [currentView, setCurrentView] = useState('home');
  const [screenLimit, setScreenLimit] = useState(null);
  const [tvPowerAssignments, setTvPowerAssignments] = useState({});
  const [tvPowerPlugStates, setTvPowerPlugStates] = useState({});
  const menuRef = useRef(null);
  const { currentUser, isAdmin } = useAuth();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const screensQuery = isAdmin 
  ? collection(db, 'screens')
  : query(collection(db, 'screens'), where('userId', '==', currentUser?.uid));

const playlistsQuery = isAdmin
  ? collection(db, 'playlists')
  : query(collection(db, 'playlists'), where('userId', '==', currentUser?.uid));

const programsQuery = isAdmin
  ? collection(db, 'programs')
  : query(collection(db, 'programs'), where('userId', '==', currentUser?.uid));

const [screensSnapshot, playlistsSnapshot, assetsSnapshot, programsSnapshot] = await Promise.all([
  getDocs(screensQuery),
  getDocs(playlistsQuery),
  getDocs(collection(db, 'assets')),
  getDocs(programsQuery)
]);

      // Fetch user's screen limit (only for non-admin)
      if (!isAdmin && currentUser?.uid) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setScreenLimit(userData.subscription?.screensAllowed || 1);
        }
      }

     const screensData = screensSnapshot.docs.map(doc => {
  const data = doc.data();
  const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
  const isOnline = data.status === 'paired' && data.lastSeen && data.lastSeen > fiveMinutesAgo;
  return {
    id: doc.id,
    ...data,
    isOnline
  };
}).filter(screen => {
  if (isAdmin) return !screen.userId;
  return screen.userId === currentUser?.uid;
});

      const playlistsData = playlistsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(playlist => {
        if (isAdmin) return !playlist.userId || playlist.userId === currentUser?.uid;
        return playlist.userId === currentUser?.uid;
      });

      const layoutsData = assetsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(asset => {
          if (asset.type !== 'layout') return false;
          if (asset.isTemplate) return false; // templates are authoring presets, not schedulable to a screen
          if (isAdmin) return !asset.userId || asset.userId === currentUser?.uid;
          return asset.userId === currentUser?.uid;
        });

      screensData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return (a.screenName || '').localeCompare(b.screenName || '');
      });

      setScreens(screensData);
      setPlaylists(playlistsData);
      setLayouts(layoutsData);
      const programsData = programsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(program => {
          if (isAdmin) return !program.userId || program.userId === currentUser?.uid;
          return program.userId === currentUser?.uid;
        });
      setPrograms(programsData);

      // Fetch TV Power config for plug badges
      if (currentUser?.uid) {
        try {
          const tvPowerDoc = await getDoc(doc(db, 'users', currentUser.uid, 'tvPower', 'config'));
          if (tvPowerDoc.exists()) {
            const tvData = tvPowerDoc.data();
            setTvPowerAssignments(tvData.assignments || {});
            setTvPowerPlugStates(tvData.plugStates || {});
          }
        } catch (tvErr) {
          console.error('Error fetching TV Power config:', tvErr);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, currentUser?.uid]);

  useEffect(() => {
  fetchData();
  
  const interval = setInterval(() => {
    fetchData();
  }, 60000);
  
  return () => clearInterval(interval);
}, [fetchData]);

  const handleViewChange = (view) => {
    setCurrentView(view);
    fetchData();
  };

  // Filter screens based on current view
  const filteredScreens = screens.filter(screen => {
    if (currentView === 'home') {
      return screen.screenStatus !== 'inactive';
    } else {
      return screen.screenStatus === 'inactive';
    }
  });

  // Count active screens (not inactive)
  const activeScreenCount = screens.filter(s => s.screenStatus !== 'inactive').length;

  // Check if at screen limit
  const isAtScreenLimit = !isAdmin && screenLimit !== null && activeScreenCount >= screenLimit;

  const handleMoveScreen = async (screen) => {
  devLog('handleMoveScreen called', screen.id, screen.screenStatus);
  setOpenMenuId(null);
  const newStatus = screen.screenStatus === 'inactive' ? 'active' : 'inactive';
  devLog('new status will be:', newStatus);
  
  // Check limit when moving to Home (active)
  if (newStatus === 'active' && isAtScreenLimit) {
    alert(`You've reached your limit of ${screenLimit} screen${screenLimit > 1 ? 's' : ''}. Upgrade your plan or move another screen to Inactive first.`);
    return;
  }
  
  try {
    devLog('updating doc...');
    await updateDoc(doc(db, 'screens', screen.id), {
      screenStatus: newStatus
    });
    devLog('update complete, fetching data...');
    fetchData();
  } catch (error) {
    console.error('Error moving screen:', error);
  }
};

const handleRestartScreen = async (screen) => {
  setOpenMenuId(null);
  try {
    await updateDoc(doc(db, 'screens', screen.id), {
      restartCommand: Date.now()
    });
  } catch (error) {
    console.error('Error sending restart command:', error);
  }
};

const handleRestartApp = async (screen) => {
  setOpenMenuId(null);
  if (!window.confirm('This will force restart the app on this device. Continue?')) return;
  try {
    await addDoc(collection(db, 'screens', screen.id, 'commands'), {
      type: 'RESTART_APP',
      status: 'pending',
      createdAt: Date.now()
    });
  } catch (error) {
    console.error('Error sending restart app command:', error);
  }
};

  const handleAddScreenClick = () => {
    if (isAtScreenLimit) {
      alert(`You've reached your limit of ${screenLimit} screen${screenLimit > 1 ? 's' : ''}. Upgrade your plan or move a screen to Inactive.`);
      return;
    }
    setShowAddModal(true);
  };

  const handleAddScreen = async () => {
    if (!newScreenName.trim()) {
      setPairingError('Please enter a screen name');
      return;
    }

    if (!pairCode.trim()) {
      setPairingError('Please enter the pairing code from the TV');
      return;
    }

    const code = pairCode.trim().toUpperCase();
    
    setIsPairing(true);
    setPairingError('');

    try {
      const pendingRef = doc(db, 'pendingPairings', code);
      const pendingDoc = await getDoc(pendingRef);

      if (!pendingDoc.exists()) {
        setPairingError('Invalid pairing code. Make sure the code matches what\'s shown on the TV.');
        setIsPairing(false);
        return;
      }

      const pendingData = pendingDoc.data();
      
      if (pendingData.status === 'claimed') {
        setPairingError('This code has already been used. Please restart the app on the TV to get a new code.');
        setIsPairing(false);
        return;
      }

      const newScreen = {
        screenName: newScreenName,
        deviceToken: pendingData.deviceToken || '',
        deviceUid: pendingData.deviceUid || null, 
        status: 'paired',
        screenStatus: 'active',
        assignedPlaylist: '',
        assignedContent: '',
        contentType: 'playlist',
        lastSeen: Date.now(),
        order: screens.length,
        userId: isAdmin ? null : (currentUser?.uid || null)
      };

      const screenDocRef = await addDoc(collection(db, 'screens'), newScreen);

      await updateDoc(pendingRef, {
        status: 'claimed',
        screenId: screenDocRef.id,
        screenName: newScreenName
      });

      setNewScreenName('');
      setPairCode('');
      setPairingError('');
      setShowAddModal(false);
      fetchData();

    } catch (error) {
      console.error('Error pairing screen:', error);
      setPairingError('Failed to pair screen. Please try again.');
    } finally {
      setIsPairing(false);
    }
  };

  const handleDeleteScreen = async (screenId) => {
    setOpenMenuId(null);
    if (window.confirm('Are you sure you want to delete this screen?')) {
      try {
        await deleteDoc(doc(db, 'screens', screenId));
        fetchData();
      } catch (error) {
        console.error('Error deleting screen:', error);
      }
    }
  };

  const handleEditScreen = (screen) => {
    setOpenMenuId(null);
    let contentType = screen.contentType || 'playlist';
    let contentId = screen.assignedContent || screen.assignedPlaylist || '';
    
    if (!screen.contentType && screen.assignedPlaylist) {
      contentType = 'playlist';
      contentId = screen.assignedPlaylist;
    }
    
    setSelectedScreen({
      ...screen,
      contentType: contentType,
      assignedContent: contentId
    });
    setShowEditModal(true);
  };

  const handleSaveScreen = async () => {
    if (!selectedScreen) return;

    try {
      const updateData = {
        screenName: selectedScreen.screenName,
        contentType: selectedScreen.contentType,
        assignedContent: selectedScreen.assignedContent || '',
        assignedPlaylist: selectedScreen.contentType === 'playlist' ? selectedScreen.assignedContent : ''
      };

      await updateDoc(doc(db, 'screens', selectedScreen.id), updateData);
      setShowEditModal(false);
      setSelectedScreen(null);
      fetchData();
    } catch (error) {
      console.error('Error updating screen:', error);
    }
  };

  const getAssignedContentName = (screen) => {
    const contentType = screen.contentType || 'playlist';
    const contentId = screen.assignedContent || screen.assignedPlaylist;
    
    if (!contentId) return 'Nothing assigned';
    
    if (contentType === 'layout') {
      const layout = layouts.find(l => l.id === contentId);
      return layout ? `Layout: ${layout.name}` : 'Nothing assigned';
    } else if (contentType === 'program') {
      const program = programs.find(p => p.id === contentId);
      return program ? `Program: ${program.name}` : 'Nothing assigned';
    } else {
      const playlist = playlists.find(p => p.id === contentId);
      return playlist ? `Playlist: ${playlist.name}` : 'Nothing assigned';
    }
  };

  // Get plug power state for a screen (returns null if no plug assigned, true/false for on/off)
  const getScreenPlugState = (screenId) => {
    const mac = Object.keys(tvPowerAssignments).find(m => tvPowerAssignments[m] === screenId);
    if (!mac) return null;
    const state = tvPowerPlugStates[mac];
    if (!state) return false;
    return state.isOn;
  };

  const toggleMenu = (screenId, e) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === screenId ? null : screenId);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      e.target.closest('tr').style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.closest('tr').style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
  e.preventDefault();
  
  if (draggedIndex === null || draggedIndex === dropIndex) {
    setDraggedIndex(null);
    setDragOverIndex(null);
    return;
  }

  const newScreens = [...filteredScreens];
  const [draggedItem] = newScreens.splice(draggedIndex, 1);
  newScreens.splice(dropIndex, 0, draggedItem);

  const updatedScreens = screens.map(s => {
    const newIndex = newScreens.findIndex(ns => ns.id === s.id);
    if (newIndex !== -1) {
      return { ...s, order: newIndex };
    }
    return s;
  });
  
  // Sort by the new order before setting state
  updatedScreens.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  
  setScreens(updatedScreens);
  setDraggedIndex(null);
  setDragOverIndex(null);

  try {
    const updatePromises = newScreens.map((screen, index) => 
      updateDoc(doc(db, 'screens', screen.id), { order: index })
    );
    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error saving order:', error);
    fetchData();
  }
};

  const closeAddModal = () => {
    setShowAddModal(false);
    setNewScreenName('');
    setPairCode('');
    setPairingError('');
  };

  if (loading) {
    return <div className="loading">Loading screens...</div>;
  }

  return (
    <div className="screens-page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '32px', flexWrap: 'wrap' }}>
          <h1 className="page-title" style={{ margin: 0, fontSize: '20px', fontWeight: 500 }}>Screens</h1>
          <div style={{ 
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span 
              onClick={() => handleViewChange('home')}
              style={{ 
                color: currentView === 'home' ? '#333' : '#e85a5a',
                cursor: 'pointer',
                fontWeight: currentView === 'home' ? 600 : 400,
                textDecoration: currentView === 'home' ? 'none' : 'underline'
              }}
            >
              Home
            </span>
            <span style={{ color: '#999' }}>/</span>
            <span 
              onClick={() => handleViewChange('inactive')}
              style={{ 
                color: currentView === 'inactive' ? '#333' : '#e85a5a',
                cursor: 'pointer',
                fontWeight: currentView === 'inactive' ? 600 : 400,
                textDecoration: currentView === 'inactive' ? 'none' : 'underline'
              }}
            >
              Inactive
            </span>
          </div>
        </div>
        <button className="btn btn-primary" style={{ padding: '9px 18px', fontSize: '13px' }} onClick={handleAddScreenClick}>
  <Plus size={16} />
  Add Screen
</button>
      </div>

      <div className="card table-container" style={{ overflow: 'visible', marginBottom: '40px' }}>
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>Status</th>
              <th>Screen Name</th>
              <th>Assigned Content</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredScreens.map((screen, index) => (
              <tr 
                key={screen.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                style={{
                  cursor: 'grab',
                  borderTop: dragOverIndex === index && draggedIndex > index ? '2px solid #d32f2f' : 'none',
                  borderBottom: dragOverIndex === index && draggedIndex < index ? '2px solid #d32f2f' : 'none',
                  background: draggedIndex === index ? '#f5f5f5' : undefined
                }}
              >
                <td style={{ cursor: 'grab', color: '#999' }}>
                  <GripVertical size={18} />
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span 
  className={`status-badge ${
    screen.screenStatus === 'inactive'
      ? 'status-inactive'
      : screen.isOnline
        ? 'status-online'
        : 'status-offline'
  }`}
  style={{
    background: 'none',
    padding: 0,
    borderRadius: 0,
    color: screen.screenStatus === 'inactive'
      ? '#999'
      : screen.isOnline
        ? '#2a9d4a'
        : '#E63946',
    fontSize: '13px',
    fontWeight: 400
  }}
>
  {screen.screenStatus === 'inactive' ? 'Inactive' : (screen.isOnline ? 'Online' : 'Offline')}
</span>
                    {getScreenPlugState(screen.id) !== null && (
                      <span style={{
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '11px',
  fontWeight: 500,
  padding: '3px 8px',
  borderRadius: '6px',
  backgroundColor: getScreenPlugState(screen.id) ? 'rgba(37, 99, 235, 0.06)' : 'rgba(0,0,0,0.03)',
  color: getScreenPlugState(screen.id) ? '#2563eb' : '#999'
}}>
                        <Plug size={12} />
                        {getScreenPlugState(screen.id) ? 'TV On' : 'TV Off'}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Monitor size={16} color="#b0b0b0" strokeWidth={1.5} />
<span style={{ fontSize: '14px', fontWeight: 500 }}>{screen.screenName}</span>
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
  {(() => {
    const contentType = screen.contentType || 'playlist';
    const contentId = screen.assignedContent || screen.assignedPlaylist;
    if (!contentId) return <span style={{ color: '#999', fontSize: '13px' }}>Not assigned</span>;
    
    const isLayout = contentType === 'layout';
    const isProgram = contentType === 'program';
    const layoutObj = isLayout ? layouts.find(l => l.id === contentId) : null;
    const isExerciseLayout = isLayout && layoutObj?.layoutContentType === 'exercise';
    const name = isLayout
      ? (layoutObj?.name || 'Unknown')
      : isProgram
        ? (programs.find(p => p.id === contentId)?.name || 'Unknown')
        : (playlists.find(p => p.id === contentId)?.name || 'Unknown');
    // content-type pill colors: playlist=blue, layout(interval)=purple,
    // layout(exercise)=green, program=amber
    const pill = isProgram
      ? { bg: 'rgba(245, 158, 11, 0.10)', fg: '#b45309' }
      : isLayout
        ? (isExerciseLayout ? { bg: 'rgba(15, 110, 86, 0.10)', fg: '#0f6e56' } : { bg: 'rgba(124, 58, 237, 0.06)', fg: '#7c3aed' })
        : { bg: 'rgba(59, 130, 246, 0.06)', fg: '#3b82f6' };
    
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '12.5px',
        fontWeight: 400,
        background: pill.bg,
        color: pill.fg
      }}>
        {isLayout ? <LayoutGrid size={13} /> : (isProgram ? <Activity size={13} /> : <Film size={13} />)}
        {name}
      </span>
    );
  })()}
</div>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
                    <button 
  style={{
    background: 'none',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    color: '#b0b0b0',
    transition: 'all 0.12s ease'
  }}
  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; e.currentTarget.style.color = '#333'; }}
  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#b0b0b0'; }}
  onClick={() => handleEditScreen(screen)}
>
  <Edit2 size={16} />
</button>
                
                    <div style={{ position: 'relative' }} ref={openMenuId === screen.id ? menuRef : null}>
                      <button 
  style={{
    background: 'none',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    color: '#b0b0b0',
    transition: 'all 0.12s ease'
  }}
  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; e.currentTarget.style.color = '#333'; }}
  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#b0b0b0'; }}
  onClick={(e) => toggleMenu(screen.id, e)}
>
  <MoreVertical size={16} />
</button>
                      {openMenuId === screen.id && (
                        <div style={{
                          position: 'absolute',
                          top: '40px',
                          right: '0',
                          backgroundColor: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          padding: '4px 0',
                          minWidth: '160px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 100
                        }}>
                          <button 
                            style={{
                              width: '100%',
                              padding: '10px 14px',
                              border: 'none',
                              backgroundColor: 'transparent',
                              color: '#333',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); handleMoveScreen(screen); }}
                          >
                            {currentView === 'home' ? 'Move to Inactive' : 'Move to Home'}
                          </button>
                          <button 
  style={{
    width: '100%',
    padding: '10px 14px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#333',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '14px'
  }}
  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
  onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => { e.stopPropagation(); handleRestartScreen(screen); }}
>
  Restart Screen
</button>

<button 
  style={{
    width: '100%',
    padding: '10px 14px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#333',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '14px'
  }}
  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
  onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => { e.stopPropagation(); handleRestartApp(screen); }}
>
  Restart App
</button>

<button 
  style={{
    width: '100%',
    padding: '10px 14px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#E63946',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '14px'
  }}
  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
  onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => { e.stopPropagation(); handleDeleteScreen(screen.id); }}
>
  Delete
</button>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredScreens.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            {currentView === 'home' 
              ? 'No screens yet. Click "Add Screen" to get started.'
              : 'No inactive screens.'
            }
          </div>
        )}
      </div>

      <div className="mobile-list">
        {filteredScreens.map((screen, index) => (
          <div 
            key={screen.id} 
            className="mobile-list-item"
            onClick={() => handleEditScreen(screen)}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', flexShrink: 0 }}>
              <span 
  className={`status-badge-small ${
    screen.screenStatus === 'inactive'
      ? 'status-inactive'
      : screen.isOnline
        ? 'status-online'
        : 'status-offline'
  }`}
  style={{
    background: 'none',
    padding: 0,
    borderRadius: 0,
    color: screen.screenStatus === 'inactive'
      ? '#999'
      : screen.isOnline
        ? '#2a9d4a'
        : '#E63946',
    fontSize: '11px',
    fontWeight: 500,
    minWidth: 0
  }}
>
  {screen.screenStatus === 'inactive' ? 'Inactive' : (screen.isOnline ? 'Online' : 'Offline')}
</span>
              {getScreenPlugState(screen.id) !== null && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  fontSize: '10px',
                  fontWeight: 500,
                  padding: '3px 6px',
                  borderRadius: '6px',
                  backgroundColor: getScreenPlugState(screen.id) ? '#dbeafe' : '#f3f4f6',
                  color: getScreenPlugState(screen.id) ? '#2563eb' : '#999'
                }}>
                  <Plug size={10} />
                  {getScreenPlugState(screen.id) ? 'On' : 'Off'}
                </span>
              )}
            </div>
            
            <div className="mobile-list-content">
              <div className="mobile-list-title">{screen.screenName}</div>
              <div className="mobile-list-subtitle">
                ASSIGNED: {getAssignedContentName(screen)}
              </div>
            </div>
            
            <div className="mobile-list-menu" ref={openMenuId === screen.id ? menuRef : null}>
              <button 
                className="more-btn"
                onClick={(e) => toggleMenu(screen.id, e)}
                aria-label="More options"
              >
                <MoreVertical size={20} />
              </button>
              
              {openMenuId === screen.id && (
                <div className={`dropdown-menu ${index === 0 ? 'dropdown-down' : ''}`}>
                  <button 
                    className="dropdown-item"
                    onClick={(e) => { e.stopPropagation(); handleEditScreen(screen); }}
                  >
                    Edit
                  </button>
                  <button 
                    className="dropdown-item"
                    onClick={(e) => { e.stopPropagation(); handleMoveScreen(screen); }}
                  >
                    {currentView === 'home' ? 'Move to Inactive' : 'Move to Home'}
                  </button>
                  <button 
  className="dropdown-item"
  onClick={(e) => { e.stopPropagation(); handleRestartScreen(screen); }}
>
  Restart Screen
</button>

<button 
  className="dropdown-item"
  onClick={(e) => { e.stopPropagation(); handleRestartApp(screen); }}
>
  Restart App
</button>
                  <button 
                    className="dropdown-item dropdown-item-danger"
                    onClick={(e) => { e.stopPropagation(); handleDeleteScreen(screen.id); }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {filteredScreens.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            {currentView === 'home' 
              ? 'No screens yet. Tap + to add one.'
              : 'No inactive screens.'
            }
          </div>
        )}
      </div>

      <button className="fab" onClick={handleAddScreenClick} aria-label="Add screen">
        <Plus size={24} />
      </button>

      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add Screen</h2>
              <button className="modal-close" onClick={closeAddModal}>×</button>
            </div>
            
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Type your pair code, which you see on your screen now.
            </p>

            <div className="form-group">
              <label className="form-label">Pair Code *</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter code from TV"
                value={pairCode}
                onChange={(e) => setPairCode(e.target.value.toUpperCase())}
                style={{ 
                  fontSize: '24px', 
                  textAlign: 'center', 
                  letterSpacing: '4px',
                  textTransform: 'uppercase'
                }}
                maxLength={6}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">Screen Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Screen 1, Lobby Display"
                value={newScreenName}
                onChange={(e) => setNewScreenName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddScreen()}
              />
            </div>

            {pairingError && (
              <div style={{ 
                color: '#d32f2f', 
                background: '#ffebee', 
                padding: '12px', 
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                {pairingError}
              </div>
            )}

            <p style={{ fontSize: '12px', color: '#999', marginBottom: '20px' }}>
              To get a pair code, install the Pulse Player app on your Fire TV or Android device.
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeAddModal}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleAddScreen}
                disabled={isPairing}
              >
                {isPairing ? 'Pairing...' : 'Pair'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && selectedScreen && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Screen</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            
            <div className="form-group">
              <label className="form-label">Screen Name</label>
              <input
                type="text"
                className="form-input"
                value={selectedScreen.screenName}
                onChange={(e) => setSelectedScreen({...selectedScreen, screenName: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Content Type</label>
              <select
                className="form-input"
                value={selectedScreen.contentType || 'playlist'}
                onChange={(e) => setSelectedScreen({
                  ...selectedScreen, 
                  contentType: e.target.value,
                  assignedContent: ''
                })}
              >
                <option value="playlist">Playlist</option>
                <option value="layout">Layout</option>
                <option value="program">Program</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                {selectedScreen.contentType === 'layout' ? 'Assigned Layout' : selectedScreen.contentType === 'program' ? 'Assigned Program' : 'Assigned Playlist'}
              </label>
              <select
                className="form-input"
                value={selectedScreen.assignedContent || ''}
                onChange={(e) => setSelectedScreen({...selectedScreen, assignedContent: e.target.value})}
              >
                <option value="">-- Select {selectedScreen.contentType === 'layout' ? 'a Layout' : selectedScreen.contentType === 'program' ? 'a Program' : 'a Playlist'} --</option>
                {selectedScreen.contentType === 'program' ? (
                  <>
                    {programs.map(program => (
                      <option key={program.id} value={program.id}>
                        {program.name}
                      </option>
                    ))}
                  </>
                ) : selectedScreen.contentType === 'layout' ? (
                  <>
                    <optgroup label="Interval">
                      {layouts.filter(l => l.layoutContentType === 'interval').map(layout => (
                        <option key={layout.id} value={layout.id}>
                          {layout.name} ({layout.zones?.length || 0} zones)
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Exercise">
                      {layouts.filter(l => l.layoutContentType === 'exercise').map(layout => (
                        <option key={layout.id} value={layout.id}>
                          {layout.name} ({layout.zones?.length || 0} zones)
                        </option>
                      ))}
                    </optgroup>
                  </>
                ) : (
                  <>
                    <optgroup label="Interval">
                      {playlists.filter(p => p.playlistContentType === 'interval').map(playlist => (
                        <option key={playlist.id} value={playlist.id}>
                          {playlist.name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Exercise">
                      {playlists.filter(p => p.playlistContentType === 'exercise').map(playlist => (
                        <option key={playlist.id} value={playlist.id}>
                          {playlist.name}
                        </option>
                      ))}
                    </optgroup>
                  </>
                )}
              </select>
            </div>

            {selectedScreen.contentType === 'layout' && selectedScreen.assignedContent && (
              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                background: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Layout Preview</div>
                <div style={{ 
                  position: 'relative', 
                  width: '100%', 
                  paddingTop: '56.25%', 
                  background: '#e0e0e0',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  {(() => {
                    const layout = layouts.find(l => l.id === selectedScreen.assignedContent);
                    if (!layout || !layout.zones) return null;
                    return layout.zones.map((zone, idx) => (
                      <div
                        key={zone.id || idx}
                        style={{
                          position: 'absolute',
                          top: `${zone.top}%`,
                          left: `${zone.left}%`,
                          width: `${zone.width}%`,
                          height: `${zone.height}%`,
                          background: `hsl(${idx * 60}, 60%, 70%)`,
                          border: '1px solid rgba(0,0,0,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          color: 'rgba(0,0,0,0.6)',
                          boxSizing: 'border-box'
                        }}
                      >
                        {zone.name}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveScreen}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Screens;